import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatNaira, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShoppingCart, ChevronDown, ChevronRight, Phone, Mail, MapPin, Truck, Store, MessageCircle } from "lucide-react";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "fulfilled", label: "Fulfilled" },
  { value: "cancelled", label: "Cancelled" },
];

const statusColor = {
  new: "bg-amber-100 text-amber-700",
  contacted: "bg-blue-100 text-blue-700",
  fulfilled: "bg-green-100 text-green-700",
  cancelled: "bg-slate-200 text-slate-600",
};

export default function AdminOrders() {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["admin-orders"],
    queryFn: () => api.get("/admin/orders").then((r) => r.data),
  });

  const counts = orders.reduce((a, o) => { const s = o.status || "new"; a[s] = (a[s] || 0) + 1; return a; }, {});
  const filtered = filter === "all" ? orders : orders.filter((o) => (o.status || "new") === filter);
  const TABS = [{ value: "all", label: "All" }, ...STATUSES];

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}`, { status });
      toast.success("Order status updated");
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-orders">
      <div className="mb-6">
        <h1 className="font-display font-black text-3xl text-slate-900">Orders</h1>
        <p className="text-slate-500">Shopping lists customers have sent from the website.</p>
      </div>

      {orders.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="order-tabs">
          {TABS.map((t) => {
            const n = t.value === "all" ? orders.length : (counts[t.value] || 0);
            return (
              <button
                key={t.value}
                onClick={() => setFilter(t.value)}
                data-testid={`order-tab-${t.value}`}
                className={`px-3 py-1.5 text-sm font-semibold rounded-lg border transition-colors ${filter === t.value ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                {t.label} <span className="opacity-70">({n})</span>
              </button>
            );
          })}
        </div>
      )}

      {isLoading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center border-2 border-dashed rounded-xl text-slate-400" data-testid="no-orders">
          <ShoppingCart className="w-10 h-10 mx-auto mb-3 opacity-40" />
          {orders.length === 0 ? "No orders yet." : "No orders in this view."}
        </div>
      ) : (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-slate-50 text-slate-500 text-sm font-semibold">
            <div className="col-span-1"></div>
            <div className="col-span-3">Customer</div>
            <div className="col-span-2">Channel</div>
            <div className="col-span-2">Fulfilment</div>
            <div className="col-span-1 text-center">Items</div>
            <div className="col-span-1">Total</div>
            <div className="col-span-2">Status</div>
          </div>
          <div className="divide-y">
            {filtered.map((o) => {
              const isOpen = expanded === o.id;
              const c = o.customer || {};
              return (
                <div key={o.id} data-testid={`order-row-${o.id}`}>
                  <div
                    className="grid grid-cols-12 gap-2 px-4 py-3 items-center cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpanded(isOpen ? null : o.id)}
                    data-testid={`order-toggle-${o.id}`}
                  >
                    <div className="col-span-1 text-slate-400">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </div>
                    <div className="col-span-3">
                      <p className="font-semibold text-slate-800">{o.customer_name || "—"}</p>
                      <p className="text-xs text-slate-400">{o.created_at ? new Date(o.created_at).toLocaleString() : ""}</p>
                    </div>
                    <div className="col-span-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${o.channel === "whatsapp" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                        {o.channel === "whatsapp" ? <MessageCircle className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                        {o.channel === "whatsapp" ? "WhatsApp" : "Email"}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${o.fulfilment === "collection" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                        {o.fulfilment === "collection" ? <Store className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
                        {o.fulfilment === "collection" ? "Collection" : "Delivery"}
                      </span>
                    </div>
                    <div className="col-span-1 text-center text-slate-600">{o.items?.length || 0}</div>
                    <div className="col-span-1 font-semibold">{formatNaira(o.total)}</div>
                    <div className="col-span-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColor[o.status] || statusColor.new}`}>
                        {STATUSES.find((s) => s.value === o.status)?.label || "New"}
                      </span>
                      <select
                        value={o.status || "new"}
                        onChange={(e) => setStatus(o.id, e.target.value)}
                        data-testid={`order-status-${o.id}`}
                        className="text-sm border rounded-lg px-2 py-1.5 bg-white"
                      >
                        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="px-4 pb-5 pt-1 bg-slate-50" data-testid={`order-detail-${o.id}`}>
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="bg-white border rounded-lg p-4 space-y-1.5 text-sm">
                          <p className="font-display font-bold text-slate-800 mb-2">Customer</p>
                          <p className="flex items-center gap-2 text-slate-600"><Phone className="w-4 h-4 text-brand-blue" /> <a href={`tel:${c.phone}`} className="hover:text-brand-blue">{c.phone || "—"}</a></p>
                          <p className="flex items-center gap-2 text-slate-600"><Mail className="w-4 h-4 text-brand-blue" /> {c.email ? <a href={`mailto:${c.email}`} className="hover:text-brand-blue">{c.email}</a> : "—"}</p>
                          <p className="flex items-start gap-2 text-slate-600"><MapPin className="w-4 h-4 text-brand-blue mt-0.5" /> {c.address || "—"}</p>
                        </div>
                        <div className="bg-white border rounded-lg p-4 text-sm">
                          <p className="font-display font-bold text-slate-800 mb-2">Order</p>
                          <p className="text-slate-600 mb-1"><strong>Fulfilment:</strong> {o.fulfilment === "collection" ? "Collection / pickup in store" : "Home / office delivery"}</p>
                          {o.note && <p className="text-slate-600"><strong>Note:</strong> {o.note}</p>}
                        </div>
                      </div>
                      <div className="bg-white border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 text-slate-500 text-left">
                            <tr><th className="px-4 py-2 font-semibold">Item</th><th className="px-4 py-2 font-semibold text-center">Qty</th><th className="px-4 py-2 font-semibold text-right">Price</th><th className="px-4 py-2 font-semibold text-right">Subtotal</th></tr>
                          </thead>
                          <tbody className="divide-y">
                            {(o.items || []).map((it, i) => (
                              <tr key={i}>
                                <td className="px-4 py-2 text-slate-700">{it.name}{it.unit ? ` — ${it.unit}` : ""}</td>
                                <td className="px-4 py-2 text-center">{it.qty}</td>
                                <td className="px-4 py-2 text-right">{formatNaira(it.price)}</td>
                                <td className="px-4 py-2 text-right font-semibold">{formatNaira((it.price || 0) * (it.qty || 1))}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-slate-50"><td colSpan={3} className="px-4 py-2 text-right font-semibold">Total</td><td className="px-4 py-2 text-right font-black text-brand-blue">{formatNaira(o.total)}</td></tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
